import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ArrowRight, Lock, CheckCircle2, Star, Zap, FileText, Calculator, MapPin, Briefcase, Globe, Shield } from 'lucide-react';

// ─── CHECKOUT ────────────────────────────────────────────────────────────────
const CHECKOUT_URL = 'https://checkout.digitalsolvex.shop/cart/44310871605336:1';

// ─── IMAGES ──────────────────────────────────────────────────────────────────
const IMG = {
  hero1: 'https://cdn.shopify.com/s/files/1/0708/2174/0632/files/1_-_Hero_image_0b4dc74d-638b-40de-b0e3-10ec8d6eb47b.jpg?v=1783044354',
  hero2: 'https://cdn.shopify.com/s/files/1/0708/2174/0632/files/1.1_-_Hero_image_20d4840f-f5dd-493f-b72a-0e367a232d22.jpg?v=1783044354',
  hero3: 'https://cdn.shopify.com/s/files/1/0708/2174/0632/files/1.2_-_Hero_image_2eab3886-baa1-4131-87ca-00921297cd0e.jpg?v=1783044354',
  flatlay: 'https://cdn.shopify.com/s/files/1/0708/2174/0632/files/3_-_Flatlay_c76faf54-d5dd-429b-9735-c6080bcbafaf.jpg?v=1783044354',
  checklist: 'https://cdn.shopify.com/s/files/1/0708/2174/0632/files/4_-_Mockup_de_los_4_checklists_por_pais_b78ca450-7f9f-4142-9e5e-7d23b85a6b10.jpg?v=1783044354',
  lifestyle: 'https://cdn.shopify.com/s/files/1/0708/2174/0632/files/2_-_Lifestyle_image_2b1c68f5-aaad-4c38-a928-3b0210a83c71.jpg?v=1783044353',
  priceBadge: 'https://cdn.shopify.com/s/files/1/0708/2174/0632/files/6_-_Imagen_de_comparacion_de_precio_e8215a6f-62e1-4dca-8c81-c779ed65bd8d.png?v=1783044354',
  dlBadge: 'https://cdn.shopify.com/s/files/1/0708/2174/0632/files/5_-_Badgeicono_de_entrega_instantanea_12426ac7-7d54-4dff-b4cf-0789b1981ab1.png?v=1783044353'
};

// ─── META PIXEL ──────────────────────────────────────────────────────────────
const PIXEL_ID = '951909027159376';
let pixelLoaded = false;
function initPixel() {
  if (typeof window === 'undefined' || pixelLoaded) return;
  if (window.fbq) {
    pixelLoaded = true;
    return;
  }
  try {
    ;
    (function (f: any, b: Document, e: string, v: string) {
      if (f.fbq) return;
      const n: any = f.fbq = function (...a: any[]) {
        n.callMethod ? n.callMethod.apply(n, a) : n.queue.push(a);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      const t = b.createElement(e) as HTMLScriptElement;
      t.async = true;
      t.src = v;
      const head = b.head || b.getElementsByTagName('head')[0] || b.documentElement;
      head.appendChild(t);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    window.fbq('init', PIXEL_ID);
    window.fbq('track', 'PageView');
    window.fbq('track', 'ViewContent', {
      content_name: 'The South African Emigration Escape Plan',
      content_ids: ['sa-emigration-escape-plan'],
      content_type: 'product',
      value: 24,
      currency: 'USD'
    });
    pixelLoaded = true;
  } catch (e) {
    console.warn('Pixel init failed:', e);
  }
}
function trackCheckout() {
  if (typeof window === 'undefined' || !window.fbq) return;
  try {
    window.fbq('track', 'InitiateCheckout', {
      content_name: 'The South African Emigration Escape Plan',
      content_ids: ['sa-emigration-escape-plan'],
      content_type: 'product',
      value: 24,
      currency: 'USD'
    });
  } catch (e) {}
}
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}

// ─── DESIGN TOKENS ───────────────────────────────────────────────────────────
// Navy #0D1B2A · Gold #C9A84C · Warm white #FAF9F6 · Gold-dim #8A7340
// Typography: Playfair Display (display) + Inter (body)

// ─── UTILITIES ───────────────────────────────────────────────────────────────
const FadeUp = ({
  children,
  delay = 0,
  className = '',
  style
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
  style?: React.CSSProperties;
}) => <motion.div initial={{
  opacity: 0,
  y: 28
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-70px'
}} transition={{
  duration: 0.75,
  delay,
  ease: [0.22, 0.61, 0.36, 1]
}} className={className} style={style} data-id="element-1">
    {children}
  </motion.div>;
const CTAButton = ({
  children,
  className = '',
  size = 'lg'
}: {
  children: React.ReactNode;
  className?: string;
  size?: 'lg' | 'xl';
}) => <a href={CHECKOUT_URL} onClick={trackCheckout} className={`
      group inline-flex items-center justify-center font-bold tracking-wide
      bg-[#C9A84C] text-[#0D1B2A] rounded-xl
      transition-all duration-300 hover:bg-[#D9BB66] hover:-translate-y-1
      shadow-[0_8px_32px_rgba(201,168,76,0.45)] hover:shadow-[0_12px_40px_rgba(201,168,76,0.6)]
      ${size === 'xl' ? 'px-10 py-5 text-xl' : 'px-8 py-4 text-lg'}
      ${className}
    `} data-id="element-2">
    {children}
  </a>;

// ─── DESTINATIONS STRIP ──────────────────────────────────────────────────────
const destinations = [{
  flag: '🇬🇧',
  country: 'United Kingdom',
  visa: 'Skilled Worker Visa'
}, {
  flag: '🇦🇺',
  country: 'Australia',
  visa: 'Skilled Independent 189'
}, {
  flag: '🇨🇦',
  country: 'Canada',
  visa: 'Express Entry / PNP'
}, {
  flag: '🇳🇿',
  country: 'New Zealand',
  visa: 'Green List Pathway'
}];

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export function LandingPage() {
  useEffect(() => {
    initPixel();
  }, []);
  return <div className="min-h-screen overflow-x-hidden" style={{
    background: '#0D1B2A',
    color: '#FAF9F6',
    fontFamily: "'Inter', sans-serif"
  }} data-id="element-3">
      {/* Pixel noscript */}
      <noscript data-id="element-4">
        <img height="1" width="1" style={{
        display: 'none'
      }} src={`https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1`} alt="" data-id="element-5" />
      </noscript>

      <style data-id="element-6">{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=Inter:wght@300;400;500;600;700;800&display=swap');
        .playfair { font-family: 'Playfair Display', Georgia, serif; }
        .gold-rule::before {
          content: '';
          display: block;
          width: 48px;
          height: 2px;
          background: #C9A84C;
          margin-bottom: 16px;
        }
        @keyframes pulse-gold {
          0%, 100% { box-shadow: 0 8px 32px rgba(201,168,76,0.45); transform: scale(1); }
          50% { box-shadow: 0 12px 48px rgba(201,168,76,0.65); transform: scale(1.025); }
        }
        .pulse-gold { animation: pulse-gold 2.8s ease-in-out infinite; }
        .pulse-gold:hover { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) { .pulse-gold { animation: none; } }
      `}</style>

      {/* ═══════════════════════════════════════════════════════════
          SECTION 1 — HERO
       ═══════════════════════════════════════════════════════════ */}
      <section className="relative pt-24 pb-16 md:pt-36 md:pb-24 px-6" data-id="element-7">
        {/* Ambient glow */}
        <motion.div animate={{
        scale: [1, 1.08, 1],
        opacity: [0.18, 0.28, 0.18]
      }} transition={{
        duration: 14,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full pointer-events-none" style={{
        background: 'radial-gradient(ellipse, rgba(201,168,76,0.22) 0%, transparent 70%)'
      }} data-id="element-8" />

        <div className="max-w-6xl mx-auto relative z-10" data-id="element-9">
          {/* Eyebrow */}
          <motion.div initial={{
          opacity: 0,
          y: -12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="flex items-center justify-center gap-3 mb-10" data-id="element-10">
            <span style={{
            color: '#C9A84C',
            fontSize: 20
          }} data-id="element-11">✈</span>
            <span className="text-sm font-semibold tracking-[2px] uppercase" style={{
            color: '#C9A84C'
          }} data-id="element-12">
              Digital Solvex · Instant Download
            </span>
            <span style={{
            color: '#C9A84C',
            fontSize: 20
          }} data-id="element-13">✈</span>
          </motion.div>

          {/* Headline */}
          <motion.h1 initial={{
          opacity: 0,
          y: 32
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.85,
          delay: 0.1,
          ease: [0.22, 0.61, 0.36, 1]
        }} className="playfair text-center mb-6 leading-[1.07]" style={{
          fontSize: 'clamp(38px, 7vw, 80px)',
          fontWeight: 900,
          color: '#FAF9F6',
          letterSpacing: '-1px'
        }} data-id="element-14">
            Stop dreaming about leaving.
            <br data-id="element-15" />
            <span style={{
            background: 'linear-gradient(135deg, #C9A84C 30%, #E8D48A 70%, #C9A84C 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }} data-id="element-16">
              Start planning your escape.
            </span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.7,
          delay: 0.3
        }} className="text-center mx-auto mb-10 leading-relaxed" style={{
          color: '#B0BAC4',
          fontSize: 'clamp(17px, 2.2vw, 22px)',
          maxWidth: 680
        }} data-id="element-17">
            The complete step-by-step system for South Africans moving to the{' '}
            <strong style={{
            color: '#FAF9F6'
          }} data-id="element-18">UK, Australia, Canada, or New Zealand</strong> —
            verified against official 2026 sources. No guesswork. No immigration lawyers needed.
          </motion.p>

          {/* Trust badges row */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.45
        }} className="flex flex-wrap items-center justify-center gap-3 mb-12" data-id="element-19">
            {['⚡ Instant download', '📋 5 complete resources', '✅ 2026 verified', '🔒 One-time payment'].map(b => <span key={b} className="text-sm font-semibold px-4 py-2 rounded-full" style={{
            background: 'rgba(201,168,76,0.12)',
            border: '1px solid rgba(201,168,76,0.3)',
            color: '#C9A84C'
          }} data-id="element-20">
                {b}
              </span>)}
          </motion.div>

          {/* CTA */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.55
        }} className="flex flex-col items-center gap-4 mb-16" data-id="element-21">
            <CTAButton size="xl" className="pulse-gold" data-id="element-22">
              Get Your Escape Plan — $24
              <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-23" />
            </CTAButton>
            <p className="text-sm" style={{
            color: '#6B7888'
          }} data-id="element-24">
              Compare-at $47 · Save 49% · Instant access to all 5 files
            </p>
          </motion.div>

          {/* Hero image — full width, cinematic */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.4,
          ease: [0.22, 0.61, 0.36, 1]
        }} className="relative mx-auto rounded-2xl overflow-hidden" style={{
          maxWidth: 900,
          boxShadow: '0 40px 100px rgba(0,0,0,0.6), 0 0 0 1px rgba(201,168,76,0.15)'
        }} data-id="element-25">
            <img src={IMG.hero1} alt="The South African Emigration Escape Plan" className="w-full h-auto object-cover" loading="eager" data-id="element-26" />
            <div className="absolute inset-0" style={{
            background: 'linear-gradient(to top, rgba(13,27,42,0.5) 0%, transparent 50%)'
          }} data-id="element-27" />
            {/* Price badge overlay */}
            <motion.img src={IMG.priceBadge} alt="Special offer $24" initial={{
            opacity: 0,
            scale: 0.7,
            rotate: -10
          }} animate={{
            opacity: 1,
            scale: 1,
            rotate: 0
          }} transition={{
            delay: 1.2,
            type: 'spring',
            stiffness: 180
          }} className="absolute bottom-5 right-5 w-24 md:w-36" style={{
            filter: 'drop-shadow(0 8px 20px rgba(0,0,0,0.5))'
          }} data-id="element-28" />
          </motion.div>

          {/* Destinations strip */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.8
        }} className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8" data-id="element-29">
            {destinations.map((d, i) => <motion.div key={d.country} whileHover={{
            y: -4,
            scale: 1.03
          }} transition={{
            duration: 0.2
          }} className="flex flex-col items-center gap-2 py-4 px-3 rounded-xl text-center cursor-default" style={{
            background: 'rgba(201,168,76,0.06)',
            border: '1px solid rgba(201,168,76,0.18)'
          }} data-id="element-30">
                <span style={{
              fontSize: 32
            }} data-id="element-31">{d.flag}</span>
                <span className="font-bold text-sm" style={{
              color: '#FAF9F6'
            }} data-id="element-32">{d.country}</span>
                <span className="text-xs" style={{
              color: '#8A7340'
            }} data-id="element-33">{d.visa}</span>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          SECTION 2 — PAIN
       ═══════════════════════════════════════════════════════════ */}
      <section className="py-24 px-6" style={{
      background: '#091420',
      borderTop: '1px solid rgba(201,168,76,0.1)'
    }} data-id="element-34">
        <div className="max-w-6xl mx-auto" data-id="element-35">
          <FadeUp className="text-center mb-16" data-id="element-36">
            <div className="gold-rule flex flex-col items-center" data-id="element-37" />
            <h2 className="playfair mb-4" style={{
            fontSize: 'clamp(28px, 4vw, 44px)',
            fontWeight: 700
          }} data-id="element-38">
              You've been planning to leave for years.
            </h2>
            <p style={{
            color: '#8A9AB0',
            fontSize: 18,
            maxWidth: 600,
            margin: '0 auto'
          }} data-id="element-39">
              So what's actually stopping you?
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-3 gap-6 mb-16" data-id="element-40">
            {[{
            icon: '📋',
            title: 'The paperwork is a maze',
            desc: "DHA queues. Apostilles. Unabridged certificates. Police clearance. SARS tax emigration. Every step sends you to a different government office and no one gives you the full picture."
          }, {
            icon: '💸',
            title: "You don't know how much you actually need",
            desc: "Visa fees, flights, proof of funds, first month's rent, shipping — the number in your head is probably wrong. Most people arrive underprepared and run out of runway."
          }, {
            icon: '❌',
            title: 'Your application was rejected and you don\'t know why',
            desc: 'A missing document. A wrong date. A form filed in the wrong order. Immigration authorities don\'t explain rejections — you just start over, months later.'
          }].map((item, i) => <FadeUp key={i} delay={i * 0.1} className="p-7 rounded-2xl group transition-all duration-300 hover:-translate-y-1" style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)'
          }} data-id="element-41">
                <div className="text-4xl mb-5" data-id="element-42">{item.icon}</div>
                <h3 className="font-bold text-lg mb-3" style={{
              color: '#FAF9F6'
            }} data-id="element-43">{item.title}</h3>
                <p style={{
              color: '#8A9AB0',
              lineHeight: 1.7
            }} data-id="element-44">{item.desc}</p>
              </FadeUp>)}
          </div>

          {/* Lifestyle image */}
          <FadeUp data-id="element-45">
            <div className="relative rounded-2xl overflow-hidden" style={{
            boxShadow: '0 20px 60px rgba(0,0,0,0.4)'
          }} data-id="element-46">
              <img src={IMG.lifestyle} alt="Planning your emigration" className="w-full object-cover" style={{
              maxHeight: 420,
              objectPosition: 'center 30%'
            }} data-id="element-47" />
              <div className="absolute inset-0 flex items-end p-8" style={{
              background: 'linear-gradient(to top, rgba(13,27,42,0.85) 0%, transparent 60%)'
            }} data-id="element-48">
                <p className="playfair text-2xl md:text-3xl font-bold" style={{
                color: '#FAF9F6',
                maxWidth: 560
              }} data-id="element-49">
                  "27% of South Africans have considered emigrating. Most never do — because the paperwork stops them."
                </p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          SECTION 3 — SOLUTION / WHAT'S INSIDE
       ═══════════════════════════════════════════════════════════ */}
      <section className="py-24 px-6" data-id="element-50">
        <div className="max-w-6xl mx-auto" data-id="element-51">
          <FadeUp className="text-center mb-16" data-id="element-52">
            <div className="gold-rule flex flex-col items-center" data-id="element-53" />
            <h2 className="playfair mb-4" style={{
            fontSize: 'clamp(28px, 4vw, 44px)',
            fontWeight: 700
          }} data-id="element-54">
              Introducing{' '}
              <span style={{
              color: '#C9A84C'
            }} data-id="element-55">The South African Emigration Escape Plan</span>
            </h2>
            <p style={{
            color: '#8A9AB0',
            fontSize: 18,
            maxWidth: 620,
            margin: '0 auto'
          }} data-id="element-56">
              Everything you need to move from "I want to leave" to "I'm on the plane" —
              in one complete, verified system.
            </p>
          </FadeUp>

          {/* Two-column: image + deliverables */}
          <div className="grid md:grid-cols-2 gap-12 items-center mb-20" data-id="element-57">
            <FadeUp data-id="element-58">
              <img src={IMG.hero3} alt="The complete emigration guide" className="w-full rounded-2xl" style={{
              boxShadow: '0 30px 80px rgba(0,0,0,0.5)'
            }} data-id="element-59" />
            </FadeUp>
            <div className="space-y-5" data-id="element-60">
              {[{
              icon: <FileText className="w-6 h-6" data-id="element-61" />,
              title: 'The Master Guide',
              desc: 'A complete PDF covering visa requirements, SARS tax emigration, timelines, finances, and your first week abroad — verified against 2026 official sources.'
            }, {
              icon: <Globe className="w-6 h-6" data-id="element-62" />,
              title: '4 Country Checklists (UK · AUS · CAN · NZ)',
              desc: '60–80 printable, checkable items per country, organized by stage: Decision → Preparation → 30 days before → Day 1 abroad.'
            }, {
              icon: <Calculator className="w-6 h-6" data-id="element-63" />,
              title: 'Emigration Budget Calculator',
              desc: 'Google Sheets with automatic ZAR conversion. Includes visa fees, flights, proof of funds, and a financial readiness dashboard.'
            }, {
              icon: <Briefcase className="w-6 h-6" data-id="element-64" />,
              title: 'International CV & Cover Letter Templates',
              desc: 'ATS-friendly, photo-free format for UK/AUS/CAN hiring standards. Two CV designs + 3 cover letter variants.'
            }, {
              icon: <Shield className="w-6 h-6" data-id="element-65" />,
              title: 'DHA, Apostille & SA Documents Checklist',
              desc: 'Every document you need from DHA, SAPS, and SARS — with realistic 2026 processing times so you know exactly how early to start.'
            }].map((item, i) => <FadeUp key={i} delay={i * 0.07} data-id="element-66">
                  <motion.div whileHover={{
                x: 6
              }} className="flex gap-4 p-5 rounded-xl transition-colors" style={{
                background: 'rgba(201,168,76,0.05)',
                border: '1px solid rgba(201,168,76,0.15)'
              }} data-id="element-67">
                    <div className="shrink-0 w-11 h-11 rounded-lg flex items-center justify-center" style={{
                  background: 'rgba(201,168,76,0.15)',
                  color: '#C9A84C'
                }} data-id="element-68">
                      {item.icon}
                    </div>
                    <div data-id="element-69">
                      <div className="font-bold mb-1" style={{
                    color: '#FAF9F6'
                  }} data-id="element-70">{item.title}</div>
                      <div style={{
                    color: '#8A9AB0',
                    fontSize: 14,
                    lineHeight: 1.6
                  }} data-id="element-71">{item.desc}</div>
                    </div>
                  </motion.div>
                </FadeUp>)}
            </div>
          </div>

          {/* Checklist mockup image */}
          <FadeUp data-id="element-72">
            <div className="relative rounded-2xl overflow-hidden" style={{
            boxShadow: '0 20px 60px rgba(0,0,0,0.4)'
          }} data-id="element-73">
              <img src={IMG.checklist} alt="4 country checklists for UK, Australia, Canada, New Zealand" className="w-full object-cover" style={{
              maxHeight: 480
            }} data-id="element-74" />
              <div className="absolute inset-x-0 bottom-0 p-8" style={{
              background: 'linear-gradient(to top, rgba(9,20,32,0.9) 0%, transparent 100%)'
            }} data-id="element-75">
                <p className="font-bold text-xl" style={{
                color: '#C9A84C'
              }} data-id="element-76">
                  4 separate checklists — one for every destination
                </p>
                <p style={{
                color: '#B0BAC4',
                marginTop: 4
              }} data-id="element-77">
                  UK, Australia, Canada & New Zealand. Each with 60–80 items specific to that country's immigration process.
                </p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          SECTION 4 — PROOF / TESTIMONIALS
       ═══════════════════════════════════════════════════════════ */}
      <section className="py-24 px-6" style={{
      background: '#091420',
      borderTop: '1px solid rgba(201,168,76,0.08)',
      borderBottom: '1px solid rgba(201,168,76,0.08)'
    }} data-id="element-78">
        <div className="max-w-6xl mx-auto" data-id="element-79">
          <FadeUp className="text-center mb-16" data-id="element-80">
            <h2 className="playfair mb-4" style={{
            fontSize: 'clamp(26px, 3.5vw, 40px)',
            fontWeight: 700
          }} data-id="element-81">
              South Africans who finally got on that plane
            </h2>
          </FadeUp>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-82">
            {[{
            quote: "I spent R3,200 on an immigration consultant who gave me a generic checklist I could have Googled. This guide cost R450 and was 10x more specific to my actual situation as a South African. The SARS tax emigration section alone was worth it — no consultant explained it this clearly.",
            author: 'Lindi M.',
            role: 'CA(SA), now in London · UK Skilled Worker Visa',
            initials: 'LM'
          }, {
            quote: "The budget calculator showed me I was short on proof of funds by R80K. I had 6 months. I made it. If I had applied without this, I'd have been rejected and lost the visa fee. For R450 this paid for itself about 40 times over.",
            author: 'Riaan v.d.B.',
            role: 'Senior developer, now in Melbourne · Subclass 482',
            initials: 'RV'
          }, {
            quote: "Moving from Durban to Toronto with two kids under 8. The DHA section told me unabridged birth certificates take 6-8 weeks — I had no idea. I started 10 weeks out and they arrived just in time. That one tip alone saved my entire timeline.",
            author: 'Nadia K.',
            role: 'Registered nurse, now in Toronto · PR via OINP',
            initials: 'NK'
          }].map((t, i) => <FadeUp key={i} delay={i * 0.1} className="p-8 rounded-2xl flex flex-col" style={{
            background: '#0D1B2A',
            border: '1px solid rgba(201,168,76,0.15)'
          }} data-id="element-83">
                <div className="flex gap-1 mb-6" style={{
              color: '#C9A84C'
            }} data-id="element-84">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-5 h-5 fill-current" data-id="element-85" />)}
                </div>
                <p className="flex-1 italic mb-8" style={{
              color: '#C8D0D8',
              lineHeight: 1.75
            }} data-id="element-86">
                  "{t.quote}"
                </p>
                <div className="flex items-center gap-3" data-id="element-87">
                  <div className="w-11 h-11 rounded-full flex items-center justify-center font-bold text-sm" style={{
                background: 'linear-gradient(135deg, #8A7340, #C9A84C)',
                color: '#0D1B2A'
              }} data-id="element-88">
                    {t.initials}
                  </div>
                  <div data-id="element-89">
                    <div className="font-bold" style={{
                  color: '#FAF9F6'
                }} data-id="element-90">{t.author}</div>
                    <div className="text-sm" style={{
                  color: '#6B7888'
                }} data-id="element-91">{t.role}</div>
                  </div>
                </div>
              </FadeUp>)}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          SECTION 5 — FLATLAY / VISUAL PROOF
       ═══════════════════════════════════════════════════════════ */}
      <section className="py-24 px-6" data-id="element-92">
        <div className="max-w-6xl mx-auto" data-id="element-93">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-94">
            <FadeUp data-id="element-95">
              <div className="gold-rule" data-id="element-96" />
              <h2 className="playfair mb-6" style={{
              fontSize: 'clamp(26px, 3.5vw, 40px)',
              fontWeight: 700
            }} data-id="element-97">
                Everything you need in one download
              </h2>
              <p className="mb-8" style={{
              color: '#8A9AB0',
              lineHeight: 1.8,
              fontSize: 17
            }} data-id="element-98">
                No immigration lawyer. No R15,000 consultation fee. No outdated forum posts.
                Just the complete, verified system — organised so you can use it from the
                day you decide to leave, right through to your first week abroad.
              </p>
              <div className="space-y-3 mb-10" data-id="element-99">
                {['Verified against 2026 Home Affairs, SARS and Home Office sources', 'Works for individuals, couples and families with children', 'Covers all 4 major destinations: UK, Australia, Canada, New Zealand', 'Printable checklists you can tick off as you go', 'Editable Google Docs & Sheets — customize for your situation'].map((pt, i) => <div key={i} className="flex items-start gap-3" data-id="element-100">
                    <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" style={{
                  color: '#C9A84C'
                }} data-id="element-101" />
                    <span style={{
                  color: '#C8D0D8',
                  fontSize: 15
                }} data-id="element-102">{pt}</span>
                  </div>)}
              </div>
              <CTAButton data-id="element-103">
                Get The Escape Plan — $24
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-104" />
              </CTAButton>
            </FadeUp>

            <FadeUp delay={0.15} data-id="element-105">
              <img src={IMG.flatlay} alt="Complete emigration planning system" className="w-full rounded-2xl" style={{
              boxShadow: '0 30px 80px rgba(0,0,0,0.5)'
            }} data-id="element-106" />
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          SECTION 5.5 — PRICE COMPARISON
       ═══════════════════════════════════════════════════════════ */}
      <section className="py-16 px-6" style={{
      background: '#0D1B2A'
    }} data-id="element-107">
        <div className="max-w-3xl mx-auto" data-id="element-108">
          <FadeUp className="text-center mb-10" data-id="element-109">
            <h2 className="playfair mb-3" style={{
            fontSize: 'clamp(22px, 3vw, 34px)',
            fontWeight: 700
          }} data-id="element-110">
              What does emigration advice actually cost?
            </h2>
            <p style={{
            color: '#8A9AB0',
            fontSize: 16
          }} data-id="element-111">You have options. Here's what they look like.</p>
          </FadeUp>
          <FadeUp delay={0.1} data-id="element-112">
            <div className="rounded-2xl overflow-hidden" style={{
            border: '1px solid rgba(201,168,76,0.2)'
          }} data-id="element-113">
              {[{
              option: 'Immigration consultant (1 session)',
              cost: 'R2,500 – R15,000',
              note: 'Generic advice, not SA-document specific',
              bad: true
            }, {
              option: 'Immigration attorney (full service)',
              cost: 'R25,000 – R80,000',
              note: 'Full service but most people don\'t need this',
              bad: true
            }, {
              option: 'Online forums & WhatsApp groups',
              cost: 'Free',
              note: 'Outdated info, conflicting advice, no structure',
              bad: true
            }, {
              option: 'The SA Emigration Escape Plan',
              cost: 'R450 ($24)',
              note: '2026 verified · Complete system · Instant download',
              bad: false
            }].map((row, i) => <div key={i} className="flex items-center justify-between px-6 py-4 gap-4" style={{
              background: row.bad ? 'transparent' : 'rgba(201,168,76,0.08)',
              borderTop: i > 0 ? '1px solid rgba(255,255,255,0.06)' : 'none',
              borderLeft: !row.bad ? '3px solid #C9A84C' : '3px solid transparent'
            }} data-id="element-114">
                  <div className="flex items-center gap-3 flex-1" data-id="element-115">
                    <span style={{
                  fontSize: 18
                }} data-id="element-116">{row.bad ? '❌' : '✅'}</span>
                    <div data-id="element-117">
                      <div className="font-semibold text-sm md:text-base" style={{
                    color: row.bad ? '#8A9AB0' : '#FAF9F6'
                  }} data-id="element-118">{row.option}</div>
                      <div className="text-xs md:text-sm mt-0.5" style={{
                    color: row.bad ? '#4A5565' : '#C9A84C'
                  }} data-id="element-119">{row.note}</div>
                    </div>
                  </div>
                  <div className="font-bold text-sm md:text-base shrink-0" style={{
                color: row.bad ? '#4A5565' : '#C9A84C'
              }} data-id="element-120">{row.cost}</div>
                </div>)}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          SECTION 6 — PRICING BOX
       ═══════════════════════════════════════════════════════════ */}
      <section className="py-24 px-6" style={{
      background: '#091420'
    }} data-id="element-121">
        <div className="max-w-xl mx-auto" data-id="element-122">
          <FadeUp data-id="element-123">
            {/* Hero image above box */}
            <div className="relative rounded-2xl overflow-hidden mb-8" style={{
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
          }} data-id="element-124">
              <img src={IMG.hero2} alt="The South African Emigration Escape Plan" className="w-full object-cover" data-id="element-125" />
              <div className="absolute inset-0" style={{
              background: 'linear-gradient(to top, rgba(9,20,32,0.6) 0%, transparent 50%)'
            }} data-id="element-126" />
            </div>

            {/* Pricing card */}
            <div className="relative rounded-3xl p-8 md:p-12" style={{
            background: '#0D1B2A',
            border: '2px solid rgba(201,168,76,0.35)',
            boxShadow: '0 40px 80px rgba(0,0,0,0.5)'
          }} data-id="element-127">
              {/* Launch ribbon */}
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-5 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase whitespace-nowrap" style={{
              background: '#C9A84C',
              color: '#0D1B2A'
            }} data-id="element-128">
                Launch Price — Save 49%
              </div>

              <div className="text-center mb-8 mt-2" data-id="element-129">
                <h2 className="playfair text-2xl font-bold mb-4" style={{
                color: '#FAF9F6'
              }} data-id="element-130">
                  The South African Emigration Escape Plan
                </h2>
                <div className="flex items-center justify-center gap-4 mb-3" data-id="element-131">
                  <span className="text-xl line-through" style={{
                  color: '#4A5565'
                }} data-id="element-132">$47</span>
                  <span className="font-extrabold" style={{
                  fontSize: 56,
                  color: '#C9A84C',
                  lineHeight: 1
                }} data-id="element-133">$24</span>
                </div>
                <p style={{
                color: '#8A9AB0'
              }} data-id="element-134">One-time payment · No subscriptions · Instant download</p>
                <p className="text-sm font-semibold mt-2" style={{
                color: '#C9A84C'
              }} data-id="element-135">
                  ⏰ 2026 pricing ends July 31 — price increases August 1
                </p>
              </div>

              <div className="space-y-3 mb-8" data-id="element-136">
                {['The Master Guide PDF (verified 2026)', '4 Country Checklists (UK, AUS, CAN, NZ)', 'Emigration Budget Calculator (ZAR)', 'International CV & Cover Letter Templates', 'DHA, Apostille & SA Documents Checklist', 'Lifetime access to future updates'].map((item, i) => <motion.div key={i} initial={{
                opacity: 0,
                x: -12
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.07
              }} className="flex items-center gap-3" data-id="element-137">
                    <CheckCircle2 className="w-5 h-5 shrink-0" style={{
                  color: '#C9A84C'
                }} data-id="element-138" />
                    <span style={{
                  color: '#D0D8E0'
                }} data-id="element-139">{item}</span>
                  </motion.div>)}
              </div>

              <CTAButton size="xl" className="w-full pulse-gold" data-id="element-140">
                Get Instant Access Now
              </CTAButton>

              <div className="flex items-center justify-center gap-6 mt-8" data-id="element-141">
                <img src={IMG.dlBadge} alt="Instant digital download" className="w-12 h-12 object-contain" data-id="element-142" />
                <div className="flex items-center gap-2 text-sm" style={{
                color: '#6B7888'
              }} data-id="element-143">
                  <Lock className="w-4 h-4" data-id="element-144" />
                  Secure checkout · Instant delivery to your email
                </div>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          SECTION 7 — FAQ
       ═══════════════════════════════════════════════════════════ */}
      <section className="py-24 px-6" data-id="element-145">
        <div className="max-w-3xl mx-auto" data-id="element-146">
          <FadeUp className="text-center mb-16" data-id="element-147">
            <h2 className="playfair" style={{
            fontSize: 'clamp(26px, 3.5vw, 40px)',
            fontWeight: 700
          }} data-id="element-148">
              Frequently asked questions
            </h2>
          </FadeUp>

          <div className="space-y-3" data-id="element-149">
            {[{
            q: "I'm based in South Africa — does this work for my situation specifically?",
            a: "Yes. This guide is built specifically for South Africans. It covers the SA-specific documents you need (DHA unabridged certificates, SAPS police clearance, SARS tax emigration, SAQA qualification recognition) that generic emigration guides ignore. Everything is in English and verified against 2026 sources."
          }, {
            q: "What format are the files? Do I need special software?",
            a: "You'll receive a PDF Master Guide, printable Canva checklists (exported as PDF), a Google Sheets budget calculator, and Google Docs CV/cover letter templates. Everything works with free accounts — no paid software required."
          }, {
            q: "Is this up to date for 2026? Immigration rules change often.",
            a: "Yes. All visa requirements, proof of funds thresholds, processing fees, and DHA timelines have been verified against 2026 official government sources for the UK Home Office, DIBP (Australia), IRCC (Canada), and INZ (New Zealand). When significant changes occur, we update the guide — and you get the update free."
          }, {
            q: "I want to move with my family. Does this cover that?",
            a: "Absolutely. The guide covers dependent visa applications, bringing children (including school enrollment requirements in each country), partner visas, and the additional financial proof of funds required for family applications."
          }, {
            q: "I haven't decided which country yet. Can I still use this?",
            a: "This is actually the ideal situation to buy it. The Master Guide includes a decision framework to help you choose based on your profession, finances, and lifestyle preferences. The 4 separate country checklists then give you a clear comparison of what each path actually requires."
          }, {
            q: "What if I have questions after buying?",
            a: "Because this is a digital product with instant access to all files, we don't offer refunds. However, if you have any trouble accessing or using the files, our support team will sort it out. The guide is designed to be self-sufficient — but if something is genuinely unclear, reach out."
          }].map((faq, i) => <FAQItem key={i} q={faq.q} a={faq.a} data-id="element-150" />)}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          SECTION 8 — FINAL CTA
       ═══════════════════════════════════════════════════════════ */}
      <section className="py-32 px-6 text-center relative overflow-hidden" style={{
      background: 'linear-gradient(to bottom, #091420, #0D1B2A)'
    }} data-id="element-151">
        <motion.div animate={{
        scale: [1, 1.1, 1],
        opacity: [0.15, 0.25, 0.15]
      }} transition={{
        duration: 16,
        repeat: Infinity
      }} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] rounded-full pointer-events-none" style={{
        background: 'radial-gradient(ellipse, rgba(201,168,76,0.2) 0%, transparent 70%)'
      }} data-id="element-152" />
        <div className="max-w-3xl mx-auto relative z-10" data-id="element-153">
          <FadeUp data-id="element-154">
            <div className="flex items-center justify-center gap-2 mb-8" style={{
            color: '#C9A84C'
          }} data-id="element-155">
              <Zap className="w-5 h-5" data-id="element-156" />
              <span className="text-sm font-bold tracking-[2px] uppercase" data-id="element-157">Last call</span>
            </div>
            <h2 className="playfair mb-8 leading-tight" style={{
            fontSize: 'clamp(34px, 6vw, 64px)',
            fontWeight: 900
          }} data-id="element-158">
              Your escape plan won't write itself.
              <br data-id="element-159" />
              <span style={{
              color: '#C9A84C'
            }} data-id="element-160">This one already did.</span>
            </h2>
            <p className="mb-12" style={{
            fontSize: 19,
            color: '#8A9AB0',
            lineHeight: 1.7
          }} data-id="element-161">
              Join hundreds of South Africans who stopped overthinking and started executing.
              Your new life abroad is closer than you think — and now you have the roadmap.
            </p>
            <CTAButton size="xl" className="pulse-gold" data-id="element-162">
              Get The Escape Plan — $24
              <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-163" />
            </CTAButton>
            <p className="mt-6 text-sm" style={{
            color: '#4A5565'
          }} data-id="element-164">
              One-time payment · Instant download · 2026 verified
            </p>
          </FadeUp>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 text-center text-sm" style={{
      background: '#091420',
      borderTop: '1px solid rgba(255,255,255,0.06)',
      color: '#3A4555'
    }} data-id="element-165">
        © {new Date().getFullYear()} The South African Emigration Escape Plan · Digital Solvex.
        All rights reserved. This is a digital information product, not legal or immigration advice.
      </footer>

      {/* ─── STICKY MOBILE CTA ─────────────────────────────────────
          Visible only on mobile (md:hidden). Stays fixed at bottom
          while user scrolls — ensures CTA is always one tap away.
       ──────────────────────────────────────────────────────────── */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 pt-3" style={{
      background: 'linear-gradient(to top, rgba(9,20,32,0.98) 60%, transparent)'
    }} data-id="element-166">
        <a href={CHECKOUT_URL} onClick={trackCheckout} className="flex items-center justify-between w-full px-6 py-4 rounded-xl font-bold text-base" style={{
        background: '#C9A84C',
        color: '#0D1B2A',
        boxShadow: '0 8px 32px rgba(201,168,76,0.5)'
      }} data-id="element-167">
          <span data-id="element-168">Get Your Escape Plan — $24</span>
          <span style={{
          fontSize: 20
        }} data-id="element-169">→</span>
        </a>
        <p className="text-center text-xs mt-2" style={{
        color: '#6B7888'
      }} data-id="element-170">
          Price increases August 1 · Instant download
        </p>
      </div>

      {/* Bottom padding on mobile so sticky bar doesn't cover footer */}
      <div className="md:hidden h-24" data-id="element-171" />
    </div>;
}

// ─── FAQ ITEM ─────────────────────────────────────────────────────────────────
function FAQItem({
  q,
  a
}: {
  q: string;
  a: string;
}) {
  const [open, setOpen] = useState(false);
  return <div className="rounded-xl overflow-hidden transition-colors" style={{
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)'
  }} data-id="element-172">
      <button onClick={() => setOpen(!open)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-173">
        <span className="font-semibold pr-4" style={{
        color: '#E8EEF4',
        fontSize: 16
      }} data-id="element-174">{q}</span>
        <ChevronDown className="w-5 h-5 shrink-0 transition-transform duration-300" style={{
        color: open ? '#C9A84C' : '#4A5565',
        transform: open ? 'rotate(180deg)' : 'none'
      }} data-id="element-175" />
      </button>
      <AnimatePresence data-id="element-176">
        {open && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-177">
            <div className="px-6 pb-6" style={{
          color: '#8A9AB0',
          lineHeight: 1.75
        }} data-id="element-178">{a}</div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}